import { useCallback } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { ClientWithAllocation, IEditAllocationFormValues } from '../../../shared/model/types';
import { buildSyncedClientAllocations, getClientIdsFromAllocations } from '../../utils';
import { useStoreActions, useStoreState } from '~/store/hooks';
import useRole from '~/store/user/hooks/useRole';

const getFieldPaths = (teamMemberIdx: number) => ({
  allocations: `teamMembersAllocations.${teamMemberIdx}.clientsAllocations` as const,
  selectedIds: `teamMembersAllocations.${teamMemberIdx}.selectedClientsIds` as const,
});

const useClientAllocationsUpdate = (teamMemberIdx: number) => {
  const { control, getValues, setValue } = useFormContext<IEditAllocationFormValues>();
  const { allocations: allocationsPath, selectedIds: selectedIdsPath } =
    getFieldPaths(teamMemberIdx);
  const { isGlobalUser } = useRole();
  const { current: currentClientOptions } = useStoreState(
    store => store.client.clientOptionsByTeam,
  );
  const { setCurrentClientOption } = useStoreActions(action => action.client);

  const { fields, replace } = useFieldArray({
    control,
    name: allocationsPath,
  });

  const updateAllocations = useCallback(
    (selectedClients: ClientWithAllocation[]) => {
      const currentAllocations = getValues(allocationsPath) ?? [];
      const nextAllocations = buildSyncedClientAllocations(selectedClients, currentAllocations);

      replace(nextAllocations);
      setValue(selectedIdsPath, getClientIdsFromAllocations(nextAllocations), {
        shouldDirty: true,
      });
    },
    [allocationsPath, getValues, replace, selectedIdsPath, setValue],
  );

  const removeClientAt = useCallback(
    (clientIdx: number) => {
      const currentAllocations = getValues(allocationsPath) ?? [];
      const removedClientId = currentAllocations[clientIdx]?.clientId;
      const nextAllocations = currentAllocations.filter((_, index) => index !== clientIdx);

      replace(nextAllocations);
      setValue(selectedIdsPath, getClientIdsFromAllocations(nextAllocations), {
        shouldDirty: true,
      });

      if (isGlobalUser && !!removedClientId) {
        setCurrentClientOption(
          currentClientOptions.filter(client => client.id !== removedClientId),
        );
      }
    },
    [
      allocationsPath,
      currentClientOptions,
      getValues,
      isGlobalUser,
      replace,
      selectedIdsPath,
      setCurrentClientOption,
      setValue,
    ],
  );

  return {
    fields,
    updateAllocations,
    removeClientAt,
  };
};

export default useClientAllocationsUpdate;
